import { i18n } from "@/utils/i18n";

/**
 * Toast 类型定义
 */
export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastOptions {
  message: string;
  type?: ToastType;
  duration?: number;
}

/**
 * Toast 组件
 * 提供全局通知功能，支持 JS 调用和 Unpoly Compiler 声明式调用
 */
export class Toast {
  private static containerId = "toast-container";
  private static defaultDuration = 3000;

  /**
   * 获取或创建 Toast 容器
   */
  private static getContainer(): HTMLElement {
    let container = document.getElementById(Toast.containerId);
    if (!container) {
      container = document.createElement("div");
      container.id = Toast.containerId;
      document.body.appendChild(container);
    }
    return container;
  }

  /**
   * 显示 Toast 消息
   */
  static show(options: ToastOptions | string) {
    const {
      message,
      type = "info",
      duration = Toast.defaultDuration,
    } = typeof options === "string" ? { message: options } : options;

    const container = Toast.getContainer();
    const toast = document.createElement("div");
    toast.className = `toast-item toast-${type}`;

    // 图标映射
    const icons = {
      success:
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
      error:
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>',
      info: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>',
      warning:
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>',
    };

    toast.innerHTML = `
      <div class="toast-icon">${icons[type]}</div>
      <div class="toast-content">${message}</div>
      <button class="toast-close" aria-label="${i18n.t("common.close")}">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
    `;

    // 绑定关闭按钮
    const closeBtn = toast.querySelector(".toast-close");
    closeBtn?.addEventListener("click", () => Toast.dismiss(toast));

    container.appendChild(toast);

    // 自动移除
    if (duration > 0) {
      setTimeout(() => Toast.dismiss(toast), duration);
    }
  }

  /**
   * 移除指定 Toast 元素
   */
  private static dismiss(toast: HTMLElement) {
    if (toast.classList.contains("is-leaving")) return;

    toast.classList.add("is-leaving");
    // 等待动画结束
    toast.addEventListener(
      "animationend",
      () => {
        toast.remove();
        // 如果容器空了，也可以考虑移除容器
        const container = document.getElementById(Toast.containerId);
        if (container && container.childNodes.length === 0) {
          // container.remove(); // 保持容器通常更好，避免频繁增删 DOM
        }
      },
      { once: true },
    );
  }

  // 快捷方法
  static success(message: string, duration?: number) {
    Toast.show({ message, type: "success", duration });
  }

  static error(message: string, duration?: number) {
    Toast.show({ message, type: "error", duration });
  }

  static info(message: string, duration?: number) {
    Toast.show({ message, type: "info", duration });
  }

  static warning(message: string, duration?: number) {
    Toast.show({ message, type: "warning", duration });
  }

  /**
   * 注册 Unpoly 编译器
   * 允许在 HTML 中使用 <div data-toast='{"message": "...", "type": "..."}'></div>
   */
  static compiler(selector = "[data-toast]") {
    up.compiler(selector, (element: Element) => {
      const el = element as HTMLElement;
      try {
        const data = up.data(el) as ToastOptions;
        if (data?.message) {
          Toast.show(data);
        } else if (el.textContent?.trim()) {
          // 如果没有 data-toast 属性的值，则尝试读取 textContent
          Toast.show({
            message: el.textContent.trim(),
            type: (el.dataset.type as ToastType) || "info",
            duration: el.dataset.duration
              ? parseInt(el.dataset.duration, 10)
              : undefined,
          });
        }
      } catch (e) {
        console.error("Failed to parse toast data", e);
      }

      // 编译器处理后移除原始元素，因为它只是一个触发器
      el.remove();
    });
  }
}
